import Bugsnag from "@bugsnag/js";
import type { UserResponseDto } from "@docket/consumer-app-client-typescript-axios";
import { redirect } from "@sveltejs/kit";
import { api, onAuthFailure } from "./api/client";
import { dbTokenStore } from "./api/tokens";
import db, { Key } from "./database/database";
import { signOutOfFirebase } from "./firebase";
import { STAY_LOGGED_IN_PW } from "./globals";
import type { Tokens, UserInfo } from "./models/Interfaces";
import { biometricAuth, forgetSignIn } from "./prefs.svelte";
import { queryClient } from "./query/client";
import { persistQueries, type Persister } from "./query/persist";
import { devicesQuery } from "./query/queries";
import { dismissDialogs } from "./ui/dialogs.svelte";
import { errorLog, warnLog } from "./utils/logger";

// The signed-in session: opening the one saved on this device, signing in, and signing out.
// Replaces the React app's isAuth/RequiresAuth, the five copies of its post-login code, and logout.

let user = $state<UserInfo | null>(null);
let opening: Promise<boolean> | undefined;
let persister: Persister | undefined;
let endHandlers: (() => void | Promise<void>)[] = [];

/** How a session started: opened from this device as the app started, or signed in. */
export type SessionStart = "opened" | "signedIn";
type StartHandler = (how: SessionStart) => void | Promise<void>;
let startHandlers: StartHandler[] = [];
/** How the current session started; null when signed out. */
let started: SessionStart | null = null;

export const session = {
  /** The signed-in user's account; null when signed out, or before init() has read it. */
  get user(): UserInfo | null {
    return user;
  },
};

/**
 * Open the session saved on this device, if there is one: unlock db(), check that it holds a user
 * and tokens, then restore the cached data. It runs once; later calls get the same answer, until
 * logout().
 */
export function init(): Promise<boolean> {
  opening ??= open();
  return opening;
}

async function open(): Promise<boolean> {
  try {
    if (!(await db().unlock(STAY_LOGGED_IN_PW))) {
      return false;
    }
    const [account, tokens] = await Promise.all([
      db().getItem<UserInfo>(Key.UserInfo),
      db().getItem<Tokens>(Key.Tokens),
    ]);
    // Accounts saved before the access/refresh token pair existed can't make requests: they sign in again.
    if (!account || !tokens) {
      return false;
    }
    user = account;
    persister = await persistQueries(queryClient);
  } catch (err) {
    errorLog(err, "Could not open the saved session");
    return false;
  }
  await sessionStarted("opened");
  return true;
}

/**
 * For the `load` of pages that need a session: without one, sign out whatever is left of it and go
 * to the login page, as the React app's RequiresAuth did.
 */
export async function requireSession(): Promise<void> {
  if (!(await init())) {
    await logout();
    redirect(307, "/");
  }
}

/** Save the user's account, e.g. after it changes. The tokens are kept apart. */
export async function saveUser(account: UserInfo): Promise<void> {
  // Drop the React app's legacy `token`, and the tokens if a login answer was passed in.
  const {
    token: _token,
    tokens: _tokens,
    ...stored
  } = account as UserInfo & { token?: unknown; tokens?: unknown };
  await db().setItem(Key.UserInfo, stored);
  user = stored;
}

export type SignedInScreen = "/signup/accountinfo" | "/signup/verifyphone" | "/home/search";

/** The screen a signed-in user goes to: their profile until it's complete, then their phone. */
export function nextScreen(
  account: Pick<
    UserInfo,
    "dob" | "legal_sex" | "first_name" | "last_name" | "verified_phone_number"
  >,
  { verifyPhone = true } = {},
): SignedInScreen {
  if (!account.dob || !account.legal_sex || !account.first_name || !account.last_name) {
    return "/signup/accountinfo";
  }
  if (verifyPhone && !account.verified_phone_number) {
    return "/signup/verifyphone";
  }
  return "/home/search";
}

/**
 * Finish signing in, whichever way the user signed in: keep their account and tokens (encrypted
 * under a new key made for them), start the session, and return the screen to go to next. Any
 * session still on this device is signed out first (the login page asks before it gets here).
 *
 * Sign in with Apple never asked for a verified phone number; it passes `verifyPhone: false`.
 */
export async function completeLogin(
  login: UserResponseDto | null | undefined,
  { verifyPhone = true } = {},
): Promise<SignedInScreen> {
  if (!login?.tokens?.access) {
    // This really shouldn't happen.
    const err = new Error("Did not get a log in result or token");
    Bugsnag.notify(err);
    throw err;
  }
  // Nothing from another session may leak into this one: not its cache, its saved data (which the
  // apps' storage would otherwise keep, and the web's re-encrypt for the new user) or its refresh
  // token. Firebase stays signed in: the new user may have just signed in with it.
  await forgetSession({ revoke: true, firebase: false });

  const { tokens, ...account } = login;
  await db().updateCryptoKey(account, STAY_LOGGED_IN_PW);
  await saveUser(account);
  await dbTokenStore.set(tokens);
  persister = await persistQueries(queryClient);
  opening = Promise.resolve(true);

  // The React app fetched the devices at sign-in, so settings could show them straight away.
  void queryClient.prefetchQuery(devicesQuery);
  // The first argument is the user id, which isn't sent to Bugsnag.
  Bugsnag.setUser("", login.email);
  await sessionStarted("signedIn");
  return nextScreen(account, { verifyPhone });
}

/**
 * Run `handler` whenever a session starts (listeners that only make sense while signed in, say),
 * and now if one has. Returns a function that unregisters it.
 */
export function onSessionStart(handler: StartHandler): () => void {
  startHandlers.push(handler);
  if (started) {
    void runStartHandler(handler, started);
  }
  return () => {
    startHandlers = startHandlers.filter((registered) => registered !== handler);
  };
}

async function sessionStarted(how: SessionStart): Promise<void> {
  started = how;
  for (const handler of startHandlers) {
    await runStartHandler(handler, how);
  }
}

async function runStartHandler(handler: StartHandler, how: SessionStart): Promise<void> {
  try {
    await handler(how);
  } catch (err) {
    errorLog(err, "A session start handler failed");
  }
}

/**
 * Run `handler` when the session ends (listeners that only make sense while signed in, say).
 * Returns a function that unregisters it.
 */
export function onSessionEnd(handler: () => void | Promise<void>): () => void {
  endHandlers.push(handler);
  return () => {
    endHandlers = endHandlers.filter((registered) => registered !== handler);
  };
}

async function endSession(): Promise<void> {
  await persister?.stop();
  persister = undefined;
  await queryClient.cancelQueries();
  queryClient.clear();
}

/** How long signing out waits on each server (the token revoke, Firebase) before giving up on it. */
export const SIGN_OUT_TIMEOUT_MS = 5_000;

/**
 * Sign out, forgetting this device's session: stop the queries, run the session's end handlers,
 * clear db() and the biometric preference, then revoke the refresh token and sign out of Firebase.
 * Other preferences (the theme, whether the app was rated, …) stay, as before.
 */
export async function logout({ revoke = true } = {}): Promise<void> {
  await forgetSession({ revoke, firebase: true });
}

/** What signing out and signing in over a session both do: forget the one on this device. */
async function forgetSession({ revoke, firebase }: { revoke: boolean; firebase: boolean }) {
  // From here on nothing this session started may refresh tokens or be sent again: a refresh that
  // answers late is thrown away, and can't sign anyone out.
  api.tokenProvider.reset();
  // Nor may a question it asked stay on screen, or be answered by the next person.
  dismissDialogs();
  // Queries can't succeed without tokens, and none of their answers may be saved after db() is cleared.
  await endSession();
  opening = undefined;
  user = null;
  started = null;
  const handlers = endHandlers;
  endHandlers = [];
  for (const handler of handlers) {
    try {
      await handler();
    } catch (err) {
      errorLog(err, "A session end handler failed");
    }
  }

  // The refresh token is read before db() is cleared; the revoke needs nothing else from it.
  let refreshToken: string | undefined;
  if (revoke) {
    try {
      refreshToken = (await dbTokenStore.get())?.refresh;
    } catch (err) {
      errorLog(err, "Could not read the refresh token to revoke it");
    }
  }

  // This device's copy goes first: the servers below can take forever to answer (Firebase's web
  // startup waits on its auth iframe, which a filtered network never loads), and no wait on them
  // may leave the user's data here.
  // There's no clearUser on the web.
  Bugsnag.setUser("", "", "");
  try {
    await forgetSignIn();
  } catch (err) {
    errorLog(err, "Could not blank the saved sign-in settings");
  }
  await db().clear();
  // Nor anything that started while this signed out, with the tokens that are gone now.
  api.tokenProvider.reset();
  try {
    await biometricAuth.reset();
  } catch (err) {
    // It's off in memory either way; signing out (or in) must go on.
    errorLog(err, "Could not forget the biometric setting");
  }

  await Promise.all([
    refreshToken &&
      withTimeout(
        api.auth.removeRefreshTokenFamily({ refreshToken }),
        "Revoking the refresh token",
      ).catch(() => {
        // Best effort: signing out often means the tokens no longer work.
      }),
    firebase &&
      withTimeout(signOutOfFirebase(), "Signing out of Firebase").catch((err: unknown) => {
        warnLog(String(err));
        Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
      }),
  ]);
  // And any dialog still up: one the still-mounted pages opened meanwhile (a failed load's error,
  // say), or one whose action is still running (a delete stuck offline), which answers once it's done.
  dismissDialogs({ running: true });
}

/** `promise`, or a rejection once it has taken longer than SIGN_OUT_TIMEOUT_MS. */
function withTimeout<T>(promise: Promise<T>, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${what} took longer than ${SIGN_OUT_TIMEOUT_MS} ms`)),
      SIGN_OUT_TIMEOUT_MS,
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// When the tokens can't be refreshed any more, sign out and start over at the login page, as before.
onAuthFailure(() => {
  void logout({ revoke: false })
    .catch((err: unknown) => errorLog(err, "Could not sign out after the tokens were rejected"))
    .finally(() => {
      window.location.href = "/";
    });
});
